export const EVENT_TYPES = [
  "page_view", "click", "filter_change", "scroll", "page_exit", "performance",
  "runtime_error", "resource_error", "registration_open", "registration_start",
  "registration_success", "registration_error",
] as const;
export type EventType = typeof EVENT_TYPES[number];
export type AnalyticsEvent = {
  type: EventType;
  session: string;
  pageId: string;
  page: string;
  device?: "mobile" | "tablet" | "desktop";
  referrer?: string;
  target?: string;
  destination?: string;
  x?: number;
  y?: number;
  scroll?: number;
  ms?: number;
  status?: number;
};

// Only public route shapes are retained; queries, fragments and unknown paths are discarded.
export function publicPath(value: unknown): string | undefined {
  if (typeof value !== "string") return;
  const pathname = value.split(/[?#]/, 1)[0];
  if (/^\/(?:$|(?:politica-[a-z-]+|aviso-legal|trabaja-con-nosotros|canal-denuncias|anexo)\/?$|islas\/[a-z-]+\/?$|cursos\/[a-zA-Z0-9_-]{1,100}\/?$)/.test(pathname)) return pathname;
}

export function trackAnalytics(type: EventType, target?: string, status?: number) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("site-analytics", { detail: { type, target, status } }));
  }
}

export function sanitizeEvent(value: unknown): AnalyticsEvent | undefined {
  if (!value || typeof value !== "object") return;
  const event = value as Record<string, unknown>;
  const page = publicPath(event.page);
  const uuid = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
  if (!page || !EVENT_TYPES.includes(event.type as EventType) ||
    typeof event.session !== "string" || !uuid.test(event.session) ||
    typeof event.pageId !== "string" || !uuid.test(event.pageId)) return;
  const result: AnalyticsEvent = { type: event.type as EventType, page, session: event.session, pageId: event.pageId };
  if (["mobile", "tablet", "desktop"].includes(String(event.device))) result.device = event.device as AnalyticsEvent["device"];
  if (typeof event.referrer === "string" && /^[a-z0-9.-]{1,150}$/i.test(event.referrer)) result.referrer = event.referrer;
  // Labels are explicit code identifiers, never DOM text or form values.
  if (typeof event.target === "string" && /^[a-zA-Z0-9_:/.-]{1,120}$/.test(event.target)) result.target = event.target;
  const destination = publicPath(event.destination);
  if (destination) result.destination = destination;
  for (const key of ["x", "y", "scroll", "ms", "status"] as const) {
    const n = event[key];
    const max = key === "ms" ? 86_400_000 : key === "status" ? 599 : 100;
    if (typeof n === "number" && Number.isFinite(n) && n >= 0 && n <= max) result[key] = Math.round(n);
  }
  return result;
}
